import React, { useState } from "react";
import { Dropdown, InputGroup, FormControl } from "react-bootstrap";
import * as FaIcons from "react-icons/fa";

// Filter and sort icons to make them more manageable and ensure they all display
const ICON_OPTIONS = Object.keys(FaIcons)
    .filter(key => key.startsWith('Fa') && key.length > 2) // Only get the Fa icons
    .sort() // Sort alphabetically
    .map(iconName => {
        const Icon = FaIcons[iconName];
        // Convert FaIconName to readable format
        const name = iconName.replace('Fa', '');
        
        return {
            name: name,
            icon: <Icon />,
            code: name, // Store just the name without Fa prefix
            component: Icon // Store the actual component for reuse
        };
    });

// Create a mapping function that can handle different icon code formats
export const getIconComponent = (iconCode) => {
    if (!iconCode) return null;
    
    // Try direct match first
    const directMatch = ICON_OPTIONS.find(opt => 
        opt.code === iconCode || 
        opt.name === iconCode
    );
    
    if (directMatch) {
        return directMatch.icon;
    }
    
    // Try case-insensitive match
    const caseInsensitiveMatch = ICON_OPTIONS.find(opt => 
        opt.code.toLowerCase() === iconCode.toLowerCase() || 
        opt.name.toLowerCase() === iconCode.toLowerCase()
    );
    
    if (caseInsensitiveMatch) {
        return caseInsensitiveMatch.icon;
    }
    
    // Try to extract name from "fas fa-xxx" format
    if (iconCode.includes('fa-')) {
        const iconPart = iconCode.split('fa-')[1].replace(/-/g, '');
        const matchingIcon = ICON_OPTIONS.find(opt => 
            opt.name.toLowerCase() === iconPart.toLowerCase()
        );
        
        if (matchingIcon) {
            return matchingIcon.icon;
        }
    }
    
    // Return text as fallback
    return iconCode;
};

const IconPicker = ({ selectedIcon, onIconSelect, isInvalid }) => {
    const [searchTerm, setSearchTerm] = useState("");

    // Filter icons based on search term
    const filteredIcons = ICON_OPTIONS.filter(icon => 
        icon.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <>
            <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" id="icon-dropdown" className="w-100 text-start d-flex align-items-center justify-content-between">
                    <span>
                        {selectedIcon ? 
                            <>
                                {getIconComponent(selectedIcon)} 
                                <span className="ms-2">{selectedIcon}</span>
                            </>
                            : "Select an icon"
                        }
                    </span>
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100 p-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <InputGroup className="mb-2">
                        <FormControl
                            placeholder="Search icons..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()} // Prevent dropdown from closing
                            id="icon-search"
                        />
                    </InputGroup>
                    <div className="d-flex flex-wrap">
                        {filteredIcons.map((icon) => (
                            <div 
                                key={icon.code} 
                                onClick={() => {
                                    onIconSelect(icon.code);
                                    document.getElementById('icon-dropdown').click(); // Close dropdown after selection
                                }}
                                className="p-2 m-1 border rounded text-center"
                                style={{ 
                                    cursor: 'pointer', 
                                    width: '40px', 
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '1.2rem',
                                    backgroundColor: selectedIcon === icon.code ? '#e9ecef' : 'transparent',
                                    transition: 'background-color 0.2s'
                                }}
                                title={icon.name}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = selectedIcon === icon.code ? '#e9ecef' : 'transparent'}
                            >
                                {icon.icon}
                            </div>
                        ))}
                    </div>
                </Dropdown.Menu>
            </Dropdown>
            {isInvalid && (
                <div className="text-danger mt-1 small">
                    Please select an icon.
                </div>
            )}
        </>
    );
};

export default IconPicker; 